import * as Yup from 'yup';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import { Grid, Typography } from '@mui/material';
import { MESSAGE_FETCH_ERROR } from '@/utils/validation-utils';
import {
  PhoneErrorKind,
  SMS_RESEND_BLOCK_SECONDS,
  extractPhoneError,
  resolvePhoneErrorKind,
} from '@/utils/phone-utils';
import { RussianPhoneNumberField } from '@/components/common/field/RussianPhoneNumberField';
import { russianPhoneFieldSchema } from '@/validation/phoneSchema';
import {
  useAddNewPhoneMutation,
  useGetSmsSettingsQuery,
  useSetNewPhoneMutation,
} from '@/services/api/settingsApiSlice';
import { useSnackbar } from 'notistack';
import React, { ChangeEvent, FC, Fragment, KeyboardEvent, RefObject } from 'react';
import type { PhoneErrorInfo } from '@/types/PhoneErrorInfo';

const validationSchema = Yup.object({
  phoneNumber: russianPhoneFieldSchema,
});

interface FormValues {
  phoneNumber: string;
}

interface Props {
  initialPhone: string;
  isAddPhone?: boolean;
  handleSubmit: (newPhoneNumber: string, cooldownSeconds?: number, blockSeconds?: number) => void;
  submitButtonRef: RefObject<HTMLButtonElement | null>;
  onLoading?: (isLoading: boolean) => void;
}

export const PhoneEntryForm: FC<Props> = (props: Props) => {
  const { submitButtonRef, initialPhone, isAddPhone, handleSubmit, onLoading } = props;
  const initialValues: FormValues = {
    phoneNumber: initialPhone,
  };
  const [setNewPhone, { isLoading: isPhoneChanging }] = useSetNewPhoneMutation();
  const [addNewPhone, { isLoading: isPhoneAdding }] = useAddNewPhoneMutation();
  const { enqueueSnackbar } = useSnackbar();

  // The block duration comes from the backend configuration;
  // the constant is only a fallback for the window before the request resolves.
  const { data: smsSettings } = useGetSmsSettingsQuery();
  const resendBlock: number = smsSettings?.resendBlockSeconds ?? SMS_RESEND_BLOCK_SECONDS;

  /**
   * Handles a failed send.
   * A resend cooldown or block is not a problem with the entered number,
   * so it advances to the code step and hands over the remaining seconds;
   * the countdown there reflects the time left.
   * Any other failure keeps the user on this step, reported wherever it belongs:
   * a rejection that names a reason is about the number, so it goes under the field,
   * while a transport or server failure is not and goes to a snackbar.
   */
  const handleSendError = (e: unknown, helpers: FormikHelpers<FormValues>, phoneNumber: string): void => {
    const phoneError: PhoneErrorInfo | undefined = extractPhoneError(e);
    const kind: PhoneErrorKind = resolvePhoneErrorKind(phoneError?.code);
    if (kind === 'cooldown') {
      handleSubmit(phoneNumber, phoneError?.secondsRemaining);
      return;
    }
    if (kind === 'block') {
      handleSubmit(phoneNumber, undefined, phoneError?.secondsRemaining ?? resendBlock);
      return;
    }
    if (phoneError?.message) {
      helpers.setFieldError('phoneNumber', phoneError.message);
      return;
    }
    enqueueSnackbar(MESSAGE_FETCH_ERROR, { variant: 'error' });
  };

  // Both flows send the code through the same request shape and differ only in the endpoint.
  const sendCode = isAddPhone ? addNewPhone : setNewPhone;

  const handleSubmitFormik = async (values: FormValues, helpers: FormikHelpers<FormValues>) => {
    if (onLoading) {
      onLoading(true);
    }
    await sendCode({ phone: values.phoneNumber })
      .unwrap()
      .then(() => {
        handleSubmit(values.phoneNumber);
      })
      .catch((e: unknown) => {
        handleSendError(e, helpers, values.phoneNumber);
      })
      .finally(() => {
        if (onLoading) {
          onLoading(false);
        }
      });
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      validationSchema={validationSchema}
      validateOnChange={false}
      onSubmit={handleSubmitFormik}>
      {(formikProps: FormikProps<FormValues>) => {
        const { values, touched, errors, submitForm, setFieldValue, setFieldTouched, setFieldError } = formikProps;
        const handleSetFieldValue = (field: string, value: unknown): void => {
          setFieldTouched(field, true, false);
          setFieldValue(field, value, false);
          setFieldError(field, undefined);
        };
        return (
          <Fragment>
            <Grid container={true} direction={'column'}>
              <Grid item={true} mb={2}>
                <Typography variant={'body1'} lineHeight={'20px'} data-testid={'phone-entry-label'}>
                  {isAddPhone ? 'Введите номер телефона:' : 'Введите новый номер телефона:'}
                </Typography>
              </Grid>
              <Grid item={true}>
                <Grid container={true}>
                  <Grid item={true} xs={12}>
                    <RussianPhoneNumberField
                      fullWidth={true}
                      name={'phoneNumber'}
                      autoFocus={true}
                      onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                        if (e.key !== 'Enter') {
                          return;
                        }
                        e.preventDefault();
                        void submitForm();
                      }}
                      value={values.phoneNumber ? String(values.phoneNumber) : ''}
                      error={touched.phoneNumber && Boolean(errors.phoneNumber)}
                      helperText={(touched.phoneNumber && errors.phoneNumber) || ' '}
                      isNotAbsoluteHelperText={true}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        handleSetFieldValue(e.target.name, e.target.value);
                      }}
                      disabled={isPhoneAdding || isPhoneChanging}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <button onClick={submitForm} hidden={true} ref={submitButtonRef}>
              {'submit'}
            </button>
          </Fragment>
        );
      }}
    </Formik>
  );
};
